import { useCallback, useEffect, useState } from 'react';
import { TOption } from '@src/types';
import { getCoupons } from '@src/services';

export const useCouponInfiniteOptions = () => {
  const [couponOptions, setCouponOptions] = useState<TOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchCouponOptions = useCallback(
    async (currentPage: number, isReset = false) => {
      setLoading(true);
      try {
        const res = await getCoupons({ page: currentPage });
        const options = res.data.batches.map((coupon) => ({
          label: `${coupon.id} - ${coupon.comment}`,
          value: coupon.id.toString(),
        }));

        setCouponOptions((prev) => {
          if (isReset) {
            return options as TOption[];
          }
          // Deduplicate based on value
          const existingValues = new Set(prev.map((opt) => opt.value));
          const newOptions = options.filter(
            (opt) => !existingValues.has(opt.value)
          );

          return [...prev, ...newOptions] as TOption[];
        });

        setHasMore(res.data.has_next);
        setTotal(res.data.total);
      } catch (error) {
        console.error('Failed to fetch coupons:', error);
        if (isReset) {
          setCouponOptions([]);
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchCouponOptions(1, true);
  }, []);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;

      setPage(nextPage);
      fetchCouponOptions(nextPage, false);
    }
  }, [loading, hasMore, page, fetchCouponOptions]);

  const refresh = useCallback(() => {
    setPage(1);
    setHasMore(true);
    fetchCouponOptions(1, true);
  }, [fetchCouponOptions]);

  return {
    couponOptions,
    loading,
    hasMore,
    total,
    loadMore,
    refresh,
  };
};
